import { Env } from '@voghion/fe-util'
import { useCookieAdv } from './use-cookie'

/**
 * 站点信息配置
 */
export type SiteConfig = {
  /**
   * 名称
   */
  name: string
  /**
   * 主域名
   */
  mainDomain: string
  /**
   * 网页基础路径
   */
  webBase: string
  /**
   * gtm id
   */
  gtmId: string
  /**
   * facebook 像素统计 id
   */
  facebookPixelId: string
  /**
   * 是否需要用户确认隐私协议
   */
  needConsent: boolean
  /**
   * 站点 logo
   */
  logo: string
  /**
   * 站点 icon
   */
  favicon: string
  /**
   * 静态文件配置
   */
  static?: SiteStaticConfig
  /**
   * 需要隐藏的分类 id
   */
  hiddenCategoryIds?: number[]
  /**
   * 美国站点需要隐藏的分类 id
   */
  usHiddenCategoryIds?: number[]
  /**
   * 站点配置的分类列表
   */
  categories?: { id: string; icon: string }[]
  /**
   * 站点对应的虚拟商品列表
   */
  goodsListId?: number
}

/**
 * 站点静态文件配置
 */
export type SiteStaticConfig = {
  /**
   * 同一个站点多套配置的索引，默认0
   */
  contentIndex?: number
}
/**
 * 站点
 */
export const SITE_VOGHION = 'voghion'
export const SITE_DESVIPS = 'desvips'
export const SITE_IPSVOGV = 'ipsvogv'
export const SITE_MAOMAOLIKE = 'maomaolike'
export const SITE_MKMKLIKE = 'mkmklike'
export const SITE_VOGVIPS = 'vogvips'
export const SITE_ECOLEIPS = 'ecoleips'
export const SITE_VOGVCELLS = 'vogvcells'
export const SITE_DESANDROID = 'desandroid'
export const SITE_CHATMAMMARY = 'chatmammary'
export const SITE_VIPTROIS = 'viptrois'

export const DOMAIN_WWW = 'www'
export const DOMAIN_US = 'us'
export const DOMAIN_DE = 'de'

export const FE_SITE_COOKIE_KEY = 'fe_site'
export const FE_DOMAIN_COOKIE_KEY = 'fe_domain'
export const DEV_SITE = SITE_VOGHION
export const DEV_DOMAIN = DOMAIN_WWW

/**
 * 国家
 */
export const COUNTRY_US = 'US'
export const COUNTRY_DE = 'DE'
export const COUNTRY_GB = 'GB'

const VOGHION_SITE_NAME = 'Voghion'
const VOGHION_MAIN_DOMAIN = 'voghion.com'

const maomaolikeCategoryModules = import.meta.glob('../assets/images/alone-site/maomaolike/*.png', {
  eager: true
})

const maomaolikeCategories: NonNullable<SiteConfig['categories']> = []


for (const [key, value] of Object.entries<any>(maomaolikeCategoryModules)) {
  const name = key.replace(/^.*[\\/]/, '').replace('.png', '')

  maomaolikeCategories.push({
    id: name,
    icon: value.default
  })
}

const logos: Record<string, { default: string }> = import.meta.glob(
  '../assets/images/header/logo*',
  {
    eager: true
  }
)
const basePath = '../assets/images/header/logo'
const hiddenCategoryIds = [760, 787, 679]

const siteConfigs = new Map<string, SiteConfig>([
  [
    SITE_VOGHION,
    {
      name: VOGHION_SITE_NAME,
      mainDomain: VOGHION_MAIN_DOMAIN,
      webBase: 'https://www.voghion.com',
      gtmId: 'GTM-KTCZG4Z',
      facebookPixelId: '725148525961077',
      needConsent: true, // 是否需要用户确认隐私协议
      logo: logos[`${basePath}.png`].default, // 主图
      favicon: '/favicon-voghion.ico',
      usHiddenCategoryIds: [
        // 二级类目
        446, 531, 1128, 503, 82, 540, 427, 520, 689, 614, 3, 427, 1128, 446,
        // 二级类目
        402, 406, 468, 509, 576, 577, 584, 586, 655, 773, 774, 775, 777, 778, 782, 783, 784, 785,
        788, 867, 869, 870, 871, 872, 873, 874, 877, 879, 1018, 1020, 1021, 1023, 1127, 1017, 780,
        878, 1022
      ]
    }
  ],
  [
    SITE_MAOMAOLIKE,
    {
      name: 'Maomaolike',
      mainDomain: 'maomaolike.com',
      webBase: 'https://www.maomaolike.com',
      gtmId: 'GTM-MB526549',
      facebookPixelId: '960734765532833',
      needConsent: false,
      logo: logos[`${basePath}-maomaolike.png`].default,
      favicon: '/favicon-maomaolike.ico',
      hiddenCategoryIds,
      goodsListId: 7617,
      categories: maomaolikeCategories
    }
  ],
  [
    SITE_VOGVIPS,
    {
      name: 'Vogvips',
      mainDomain: 'vogvips.com',
      webBase: 'https://www.vogvips.com',
      gtmId: 'GTM-W4CKFWHS',
      facebookPixelId: '429825629540193',
      needConsent: false,
      logo: logos[`${basePath}-vogvips.png`].default,
      favicon: '/favicon-vogvips.ico',
      hiddenCategoryIds,
      goodsListId: 7618
    }
  ],
])

/**
 * 平台名称
 * @returns
 */
export const usePlatformName = () => {
  const site = siteConfigs.get(SITE_VOGHION)
  return site?.name || VOGHION_SITE_NAME
}

/**
 * 平台名称，小写
 * @returns
 */
export const usePlatformNameLowerCase = () => {
  return usePlatformName().toLowerCase()
}

/**
 * 获取当前请求的站点
 */
export const useRequestSite = () => {
  const feSite = useCookieAdv(FE_SITE_COOKIE_KEY)
  if (feSite.value) {
    const site = siteConfigs.get(feSite.value)
    if (site) {
      return feSite.value
    }
  }
  // 去掉nuxt的判断， R-TODO 后续需要优化
  if (process.env.NODE_ENV === 'development') {
    return DEV_SITE
  }
  return SITE_VOGHION
}

/**
 * 是否是 voghion
 * @returns
 */
export const useIsVoghion = () => {
  const requestSite = useRequestSite()
  return requestSite === SITE_VOGHION
}

export const useIsMaomaolike = () => {
  const requestSite = useRequestSite()
  return requestSite === SITE_MAOMAOLIKE
}

export const useIsVogvips = () => {
  const requestSite = useRequestSite()
  return requestSite === SITE_VOGVIPS
}
/**
 * 获取请求的 env 对象
 */
export const useRequestEnv = () => {
  return Env.getInstance()
}

/**
 * 获取站点配置
 * @param site
 * @returns
 */
export const useSiteConfig = (site: string) => {
  return siteConfigs.get(site) as SiteConfig
}

/**
 * 获取当前请求的站点配置
 * @returns
 */
export const useRequestSiteConfig = () => {
  const site = useRequestSite()
  return useSiteConfig(site)
}

/**
 * 获取当前请求的站点主域名
 * @returns
 */
export const useRequestSiteMainDomain = () => {
  const siteConfig = useRequestSiteConfig()
  return siteConfig?.mainDomain || VOGHION_MAIN_DOMAIN
}

/**
 * 是否需要适配美国，主要用于在美国站上线前的适配
 */
export const useNeedAdaptToUS = () => {
  return useIsMaomaolike()
}
